import React, { useCallback, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  message,
  Modal,
  Progress,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd'
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined'
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined'
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined'
import CloudSyncOutlined from '@ant-design/icons/CloudSyncOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import MinusCircleOutlined from '@ant-design/icons/MinusCircleOutlined'
import ThunderboltOutlined from '@ant-design/icons/ThunderboltOutlined'

import { AxiosError } from 'axios'

import { dismissVcnBulkJobCurrent } from '../../api/vcn-management-api-client'
import {
  VcnBulkJobState,
  type VcnBulkJobStatus,
} from '../../api/dto/vcn-bulk-job.dto'

const { Paragraph, Text, Title } = Typography

const STAT_WRAP: React.CSSProperties = {
  minHeight: 96,
  padding: '16px 18px',
  borderRadius: 10,
  background: '#f8fafc',
  border: '1px solid #eef2f7',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}

const STAT_VALUE: React.CSSProperties = {
  fontSize: 26,
  fontWeight: 600,
  lineHeight: 1.15,
}

const STAT_TITLE: React.CSSProperties = {
  fontSize: 13,
  color: 'rgba(0, 0, 0, 0.55)',
  marginBottom: 6,
  whiteSpace: 'normal',
  lineHeight: 1.35,
}

function jobStateTag(state: VcnBulkJobState) {
  switch (state) {
    case VcnBulkJobState.QUEUED:
      return (
        <Tag icon={<CloudSyncOutlined />} color="geekblue">
          Queued
        </Tag>
      )
    case VcnBulkJobState.RUNNING:
      return (
        <Tag icon={<LoadingOutlined spin />} color="processing">
          Running
        </Tag>
      )
    case VcnBulkJobState.COMPLETED:
      return (
        <Tag icon={<CheckCircleOutlined />} color="success">
          Completed
        </Tag>
      )
    case VcnBulkJobState.TIMEOUT:
      return (
        <Tag icon={<ClockCircleOutlined />} color="warning">
          Timed out
        </Tag>
      )
    case VcnBulkJobState.FAILED:
      return (
        <Tag icon={<CloseCircleOutlined />} color="error">
          Failed
        </Tag>
      )
    default:
      return <Tag>{state}</Tag>
  }
}

export interface VcnManagementJobProgressModalProps {
  open: boolean
  onClose: () => void
  job: VcnBulkJobStatus | null
  loading: boolean
  error: string | null
  onRefresh: () => void
}

export function VcnManagementJobProgressModal({
  open,
  onClose,
  job,
  loading,
  error,
  onRefresh,
}: VcnManagementJobProgressModalProps) {
  const [dismissing, setDismissing] = useState(false)

  const handleDismiss = useCallback(async () => {
    setDismissing(true)
    try {
      await dismissVcnBulkJobCurrent()
      await Promise.resolve(onRefresh())
      onClose()
    } catch (e) {
      const status = (e as AxiosError).response?.status
      if (status === 404) {
        await Promise.resolve(onRefresh())
        onClose()
      } else {
        void message.error('Failed to dismiss job — please try again.')
      }
    } finally {
      setDismissing(false)
    }
  }, [onClose, onRefresh])

  const pct = useMemo(
    () =>
      job && job.totalCount > 0
        ? Math.round(
            (100 * (job.completedCount + job.failedCount)) / job.totalCount
          )
        : 0,
    [job]
  )

  const remaining = useMemo(
    () =>
      job && job.totalCount > 0
        ? Math.max(0, job.totalCount - job.completedCount - job.failedCount)
        : 0,
    [job]
  )

  const isDismissable =
    job?.state === VcnBulkJobState.COMPLETED ||
    job?.state === VcnBulkJobState.FAILED ||
    job?.state === VcnBulkJobState.TIMEOUT

  const progressStatus = useMemo(() => {
    if (!job) return 'normal' as const
    if (
      job.state === VcnBulkJobState.RUNNING ||
      job.state === VcnBulkJobState.QUEUED
    )
      return 'active' as const
    if (job.state === VcnBulkJobState.COMPLETED) return 'success' as const
    return 'exception' as const
  }, [job])

  return (
    <Modal
      title="Bulk VCN job progress"
      open={open}
      onCancel={onClose}
      width={780}
      styles={{ body: { paddingTop: 12 } }}
      footer={
        <Space wrap>
          {isDismissable ? (
            <Button
              type="primary"
              loading={dismissing}
              onClick={() => void handleDismiss()}
            >
              Dismiss
            </Button>
          ) : null}
          <Button onClick={() => void onRefresh()} disabled={loading}>
            Refresh
          </Button>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
    >
      <Paragraph type="secondary" style={{ marginBottom: 20 }}>
        Shared bulk termination progress for all admins. Counts update while the
        job is queued or running.
      </Paragraph>

      {error ? (
        <Alert type="error" message={error} style={{ marginBottom: 16 }} />
      ) : null}

      {loading && !job ? (
        <Alert type="info" message="Loading job status…" showIcon />
      ) : null}

      {!loading && !job ? (
        <Alert
          type="info"
          message="No bulk job in progress"
          description="Run a bulk upload and confirm actions, or wait for a colleague to start one."
        />
      ) : null}

      {job ? (
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <Card size="small" styles={{ body: { padding: '18px 20px' } }}>
            <Row align="middle" gutter={[16, 12]}>
              <Col xs={24} md={14}>
                <Space align="center" wrap size={10}>
                  {jobStateTag(job.state)}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Job ID
                  </Text>
                </Space>
                <div
                  style={{
                    marginTop: 8,
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: 12,
                    wordBreak: 'break-all',
                    color: 'rgba(0,0,0,0.75)',
                  }}
                >
                  {job.jobId}
                </div>
              </Col>
              <Col xs={24} md={10} style={{ textAlign: 'right' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Overall completion
                </Text>
                <Title level={3} style={{ margin: '4px 0 0' }}>
                  {pct}%
                </Title>
              </Col>
            </Row>

            <Divider style={{ margin: '16px 0' }} />

            <Progress
              percent={pct}
              status={progressStatus}
              strokeColor={
                job.state === VcnBulkJobState.COMPLETED
                  ? undefined
                  : { from: '#1677ff', to: '#69b1ff' }
              }
              strokeWidth={14}
              style={{ marginBottom: 0 }}
            />
          </Card>

          <div>
            <Text strong style={{ fontSize: 14, letterSpacing: 0.2 }}>
              Totals
            </Text>
            <Paragraph
              type="secondary"
              style={{ margin: '6px 0 14px', maxWidth: 640 }}
            >
              High-level counts for this run. Each tile is independent so labels
              stay readable at a glance.
            </Paragraph>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <div style={STAT_WRAP}>
                  <span
                    style={{ ...STAT_TITLE, display: 'block', marginBottom: 6 }}
                  >
                    Total in job
                  </span>
                  <Statistic value={job.totalCount} valueStyle={STAT_VALUE} />
                </div>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <div style={STAT_WRAP}>
                  <Space size={8} style={{ marginBottom: 6 }}>
                    <CheckCircleOutlined
                      style={{ color: '#1677ff', fontSize: 14 }}
                    />
                    <span style={STAT_TITLE}>Completed</span>
                  </Space>
                  <Statistic
                    value={job.completedCount}
                    valueStyle={{ ...STAT_VALUE, color: '#1677ff' }}
                  />
                </div>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <div style={STAT_WRAP}>
                  <Space size={8} style={{ marginBottom: 6 }}>
                    <MinusCircleOutlined
                      style={{ color: '#d48806', fontSize: 14 }}
                    />
                    <span style={STAT_TITLE}>Remaining</span>
                  </Space>
                  <Statistic
                    value={remaining}
                    valueStyle={{
                      ...STAT_VALUE,
                      color: remaining > 0 ? '#d48806' : '#389e0d',
                    }}
                  />
                </div>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <div style={STAT_WRAP}>
                  <Space size={8} style={{ marginBottom: 6 }}>
                    <CloseCircleOutlined
                      style={{
                        color:
                          job.failedCount > 0 ? '#cf1322' : 'rgba(0,0,0,0.25)',
                        fontSize: 14,
                      }}
                    />
                    <span style={STAT_TITLE}>Failed</span>
                  </Space>
                  <Statistic
                    value={job.failedCount}
                    valueStyle={{
                      ...STAT_VALUE,
                      color: job.failedCount > 0 ? '#cf1322' : undefined,
                    }}
                  />
                </div>
              </Col>
            </Row>
          </div>

          <div>
            <Text strong style={{ fontSize: 14, letterSpacing: 0.2 }}>
              By action type
            </Text>
            <Paragraph
              type="secondary"
              style={{ margin: '6px 0 14px', maxWidth: 640 }}
            >
              How many finished items were plain terminations,
              terminate-and-new, or create-new.
            </Paragraph>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <div
                  style={{
                    ...STAT_WRAP,
                    background:
                      'linear-gradient(135deg, #f0f5ff 0%, #fafbff 100%)',
                    borderColor: '#d6e4ff',
                  }}
                >
                  <Statistic
                    title={
                      <span style={STAT_TITLE}>
                        Terminate only
                        <Text
                          type="secondary"
                          style={{ display: 'block', fontWeight: 400 }}
                        >
                          Cards closed without replacement
                        </Text>
                      </span>
                    }
                    value={job.terminateCompleted}
                    valueStyle={{ ...STAT_VALUE, color: '#1d39c4' }}
                    prefix={<ThunderboltOutlined style={{ marginRight: 8 }} />}
                  />
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div
                  style={{
                    ...STAT_WRAP,
                    background:
                      'linear-gradient(135deg, #f6ffed 0%, #fcfffa 100%)',
                    borderColor: '#d9f7be',
                  }}
                >
                  <Statistic
                    title={
                      <span style={STAT_TITLE}>
                        Terminate + new card
                        <Text
                          type="secondary"
                          style={{ display: 'block', fontWeight: 400 }}
                        >
                          Replacement VCN issued after close
                        </Text>
                      </span>
                    }
                    value={job.terminateAndNewCompleted}
                    valueStyle={{ ...STAT_VALUE, color: '#237804' }}
                    prefix={<ThunderboltOutlined style={{ marginRight: 8 }} />}
                  />
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div
                  style={{
                    ...STAT_WRAP,
                    background:
                      'linear-gradient(135deg, #fff7e6 0%, #fffdf5 100%)',
                    borderColor: '#ffd591',
                  }}
                >
                  <Statistic
                    title={
                      <span style={STAT_TITLE}>
                        Create new only
                        <Text
                          type="secondary"
                          style={{ display: 'block', fontWeight: 400 }}
                        >
                          New VCN issued, old card kept
                        </Text>
                      </span>
                    }
                    value={job.createNewCompleted}
                    valueStyle={{ ...STAT_VALUE, color: '#d46b08' }}
                    prefix={<ThunderboltOutlined style={{ marginRight: 8 }} />}
                  />
                </div>
              </Col>
            </Row>
          </div>

          {job.state === VcnBulkJobState.TIMEOUT ? (
            <Alert
              type="warning"
              showIcon
              message="Job timed out"
              description="This job exceeded the processing time limit. It may still be completing in the background — this page will update automatically when it finishes. You can dismiss now to unblock new jobs."
            />
          ) : null}
          {job.message && job.state !== VcnBulkJobState.TIMEOUT ? (
            <Alert type="info" message={job.message} showIcon />
          ) : null}
        </Space>
      ) : null}
    </Modal>
  )
}
